import { useEffect, useState } from "react";
import { Loader2, Shirt, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { Th, Td } from "../tabHelpers";

export const WORKWEAR_TYPES = [
  { value: "polo_s", label: "Póló - S" },
  { value: "polo_m", label: "Póló - M" },
  { value: "polo_l", label: "Póló - L" },
  { value: "polo_xl", label: "Póló - XL" },
  { value: "worker_cap", label: "Dolgozói sapka" },
  { value: "manager_cap", label: "Vezetői sapka" },
];

const labelOf = (v) =>
  WORKWEAR_TYPES.find((t) => t.value === v)?.label || v || "—";

export const WORKWEAR_STATUS_TONES = {
  issued:
    "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-400",
  returned: "border-border bg-muted text-muted-foreground",
};

export function WorkwearStatusBadge({ value }) {
  const label = value === "returned" ? "Visszavéve" : "Kiadva";
  return (
    <span
      data-testid={`workwear-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        WORKWEAR_STATUS_TONES[value] || WORKWEAR_STATUS_TONES.returned
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          value === "returned" ? "bg-muted-foreground/60" : "bg-blue-500"
        )}
      />
      {label}
    </span>
  );
}

/* -------------------- Form dialog -------------------- */
const emptyForm = { item_type: "polo_m", quantity: "", issuer: "", issued_at: "" };

export function WorkwearFormDialog({ open, onOpenChange, employee, onSaved }) {
  const { user } = useAuth();
  const currentUserName = user?.name || user?.email || "—";
  const [form, setForm] = useState(emptyForm);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm, issuer: currentUserName });
    setNotes("");
    setErr("");
  }, [open, currentUserName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const qty = parseInt(form.quantity, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      setErr("A darabszám legalább 1 legyen.");
      return;
    }
    setSaving(true);
    try {
      if (!employee?._id) throw new Error("Hiányzó munkavállaló.");
      const payload = {
        item_type: form.item_type,
        quantity: qty,
        issuer: form.issuer.trim(),
        issued_at: form.issued_at,
        notes: notes || null,
      };
      const { data } = await api.post(
        `/employees/${employee._id}/workwear`,
        payload
      );
      toast.success("Munkaruha rögzítve.");
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="workwear-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Új munkaruha kiadás</DialogTitle>
          <DialogDescription>
            Add meg a kiadott munkaruha részleteit. Az új tétel automatikusan
            „Kiadva” státuszú lesz.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Típus *" testId="workwear-type">
              <Select
                value={form.item_type}
                onValueChange={(v) => setForm((f) => ({ ...f, item_type: v }))}
              >
                <SelectTrigger data-testid="workwear-type-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORKWEAR_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Darabszám *" testId="workwear-qty">
              <Input
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={form.quantity}
                onChange={(e) =>
                  setForm((f) => ({ ...f, quantity: e.target.value }))
                }
                placeholder="1"
                className="font-mono"
              />
            </Field>
          </div>

          <Field label="Időpont *" testId="workwear-issued-at">
            <DateInput
              value={form.issued_at}
              onChange={(v) =>
                setForm((f) => ({ ...f, issued_at: v }))
              }
            />
          </Field>

          <Field label="Kiadó (automatikus)" testId="workwear-issuer">
            <div
              data-testid="workwear-issuer-display"
              className="flex h-9 items-center rounded-md border border-border bg-muted/40 px-3 text-sm"
            >
              <span className="truncate">{form.issuer || "—"}</span>
            </div>
          </Field>

          <Field label="Megjegyzés" testId="workwear-notes">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Pl. szín, méret eltérés, stb."
            />
          </Field>

          {err && (
            <div
              data-testid="workwear-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="workwear-cancel"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="workwear-submit">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Section list + table -------------------- */
export function WorkwearSection({
  items,
  loading,
  onCreate,
  onSelect,
  onDelete,
}) {
  return (
    <section
      data-testid="workwear-section"
      className="space-y-5"
    >
      <header className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-muted-foreground/80">
              <Shirt className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                Munkaruha
              </span>
            </div>
            <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-[22px]">
              Kiadott munkaruha
            </h2>
          </div>
          <Button
            size="sm"
            data-testid="new-workwear-button"
            onClick={onCreate}
            className="self-start"
          >
            <Plus className="h-4 w-4" />
            Új kiadás
          </Button>
        </div>
      </header>

      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Munkaruhák betöltése…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="workwear-empty-state"
            className="flex flex-col items-center gap-3 px-6 py-14 text-center"
          >
            <div className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
              <Shirt className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">Nincs rögzített munkaruha</p>
            <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
              Itt jelennek meg a kiadott pólók és sapkák.
            </p>
            <Button variant="outline" size="sm" className="mt-2 h-8" onClick={onCreate}>
              <Plus className="h-3.5 w-3.5" /> Új kiadás
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="workwear-table">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Típus</Th>
                  <Th className="text-right">Darab</Th>
                  <Th>Kiadó</Th>
                  <Th>Időpont</Th>
                  <Th>Állapot</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it._id}
                    data-testid={`workwear-row-${it._id}`}
                    onClick={() => onSelect && onSelect(it)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td>
                      <span className="font-medium">{labelOf(it.item_type)}</span>
                    </Td>
                    <Td className="text-right font-mono text-[12.5px]">
                      {it.quantity}
                    </Td>
                    <Td>{it.issuer}</Td>
                    <Td className="text-muted-foreground">
                      {formatDateHu(it.issued_at)}
                    </Td>
                    <Td>
                      <WorkwearStatusBadge value={it.status} />
                    </Td>
                    <Td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[11px] text-muted-foreground">
                        {it.status === "issued" ? "Aktív" : "Visszavéve"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* -------------------- Detail dialog (with Visszavétel) -------------------- */
export function WorkwearDetailDialog({
  open,
  onOpenChange,
  item,
  returning,
  onReturn,
  onDelete,
}) {
  const [returnMode, setReturnMode] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      setReturnMode(false);
      setNotes("");
    }
  }, [open]);

  if (!item) return null;
  const isIssued = item.status === "issued";

  const submitReturn = async () => {
    const ok = await onReturn(item, notes.trim() || null);
    if (ok) {
      setReturnMode(false);
      setNotes("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="workwear-detail-dialog"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span>Munkaruha részletei</span>
            <WorkwearStatusBadge value={item.status} />
          </DialogTitle>
          <DialogDescription>
            Itt visszaveheted a kiadott munkaruha tételt.
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <InfoRow label="Típus">{labelOf(item.item_type)}</InfoRow>
          <InfoRow label="Darabszám">
            <span className="font-mono">{item.quantity}</span>
          </InfoRow>
          <InfoRow label="Kiadó">{item.issuer || "—"}</InfoRow>
          <InfoRow label="Időpont">{formatDateHu(item.issued_at)}</InfoRow>
          {item.notes && (
            <InfoRow label="Megjegyzés" wide>
              <span className="whitespace-pre-wrap">{item.notes}</span>
            </InfoRow>
          )}
          {item.status === "returned" && (
            <>
              <InfoRow label="Visszavette" wide>
                {item.returned_by_name || "—"}
                {item.returned_at && (
                  <span className="ml-1 text-muted-foreground">
                    · {formatDateHu(item.returned_at)}
                  </span>
                )}
              </InfoRow>
              {item.return_notes && (
                <InfoRow label="Visszavételi megjegyzés" wide>
                  <span className="whitespace-pre-wrap">{item.return_notes}</span>
                </InfoRow>
              )}
            </>
          )}
        </dl>

        {isIssued && returnMode && (
          <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50/40 p-3 dark:border-blue-500/25 dark:bg-blue-500/5">
            <label
              htmlFor="workwear-return-notes"
              className="block text-xs font-medium text-blue-700 dark:text-blue-400"
            >
              Visszavételi megjegyzés (opcionális)
            </label>
            <Textarea
              id="workwear-return-notes"
              data-testid="workwear-return-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Pl. állapot, hiányosság."
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setReturnMode(false);
                  setNotes("");
                }}
              >
                Mégse
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="workwear-return-confirm"
                onClick={submitReturn}
                disabled={returning}
              >
                {returning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Visszavétel megerősítése
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {isIssued && !returnMode ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Bezárás
              </Button>
              <Button
                type="button"
                data-testid="workwear-detail-return"
                onClick={() => setReturnMode(true)}
              >
                Visszavétel
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Bezárás
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function InfoRow({ label, wide, children }) {
  return (
    <div className={cn("space-y-1", wide && "sm:col-span-2")}>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <div className="text-[13.5px]">{children}</div>
    </div>
  );
}
